const MAX_IMAGE_DIM = 560;
const IMAGE_QUALITY = 0.55;
const MAX_VIDEO_SIZE = 5 * 1024 * 1024; // 5MB base64 limit for GUN storage

// Avatar photos are stored as a dedicated GUN field — keep them tiny (~4-8KB)
// so the relay sync never drops them due to large-value issues.
const AVATAR_MAX_DIM = 128;
const AVATAR_QUALITY = 0.6;

export async function compressAvatarPhoto(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = e => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(AVATAR_MAX_DIM / Math.max(img.width, img.height), 1);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', AVATAR_QUALITY));
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
}

export async function compressImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = e => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(MAX_IMAGE_DIM / Math.max(img.width, img.height), 1);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', IMAGE_QUALITY));
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
}

export async function compressVideo(file: File): Promise<{ videoData: string; thumbnail: string }> {
  return new Promise((resolve, reject) => {
    // Check size — if small enough, use as-is
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = e => {
      const dataUrl = e.target?.result as string;

      // Generate thumbnail from first frame
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;

      video.onloadeddata = () => {
        video.currentTime = 0.5; // seek to 0.5s for better thumbnail
      };

      video.onseeked = () => {
        const canvas = document.createElement('canvas');
        const scale = Math.min(MAX_IMAGE_DIM / Math.max(video.videoWidth, video.videoHeight), 1);
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        canvas.getContext('2d')!.drawImage(video, 0, 0, canvas.width, canvas.height);
        const thumbnail = canvas.toDataURL('image/jpeg', 0.6);

        // Check if video data is within size limit
        if (dataUrl.length > MAX_VIDEO_SIZE) {
          // Too large — return thumbnail only with a flag
          resolve({ videoData: '', thumbnail });
        } else {
          resolve({ videoData: dataUrl, thumbnail });
        }

        URL.revokeObjectURL(video.src);
      };

      video.onerror = () => reject(new Error('Failed to process video'));
      video.src = URL.createObjectURL(file);
    };
    reader.readAsDataURL(file);
  });
}

export function isVideoFile(file: File): boolean {
  return file.type.startsWith('video/');
}

export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

export type MediaType = 'image' | 'video';

export function detectMediaType(file: File): MediaType {
  return isVideoFile(file) ? 'video' : 'image';
}
